import React, { Component } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Label,
  Spinner,
  Table,
  Button,
  Badge,
  Progress,
} from "reactstrap";
import { withRouter, Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import PeriodFilter, { lastMonthToToday, periodToParams } from "../../components/Common/PeriodFilter";
import { getCouponAnalyticsReport, exportCouponAnalytics } from "../../Endpoint/Admin";
import { downloadBase64File } from "../../utils/exportToExcel";
import { toast } from "react-toastify";

class CouponAnalytics extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      period: lastMonthToToday(),
      analyticsData: null,
      breadcrumbItems: [
        { title: "Analytics", link: "/analytics" },
        { title: "Coupon Analytics", link: "#" },
      ],
    };
  }

  componentDidMount() {
    this.loadAnalytics();
  }

  loadAnalytics = async () => {
    this.setState({ loading: true });
    const { period } = this.state;

    const response = await getCouponAnalyticsReport(periodToParams(period));
    this.setState({
      analyticsData: response?.success ? response.data : null,
      loading: false,
    });
  };

  handlePeriodChange = (period) => {
    this.setState({ period }, this.loadAnalytics);
  };

  handleExport = async () => {
    this.setState({ exporting: true });
    try {
      const { period } = this.state;
      const res = await exportCouponAnalytics(periodToParams(period));
      if (res?.success && res.data) {
        downloadBase64File(res.data.base64, res.data.fileName);
      } else {
        toast.error("Export failed");
      }
    } catch (err) {
      toast.error("Export failed");
    }
    this.setState({ exporting: false });
  };

  formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  formatNumber = (num) => {
    return new Intl.NumberFormat("en-US").format(num || 0);
  };

  getCategoryBadgeColor = (category) => {
    const colors = {
      food: "success",
      room: "warning",
      transport: "info",
      all: "primary",
    };
    return colors[category?.toLowerCase()] || "secondary";
  };

  render() {
    const { loading, period, analyticsData } = this.state;

    const summary = analyticsData?.summary || {};
    const topCoupons = analyticsData?.topCoupons || [];
    const usageByCategory = analyticsData?.usageByCategory || [];
    const usageTrend = analyticsData?.usageTrend || [];

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs title="Coupon Analytics" breadcrumbItems={this.state.breadcrumbItems} />

            {/* Filters */}
            <Card>
              <CardBody>
                <Row className="align-items-end">
                  <Col md={8} className="mb-3 mb-md-0">
                    <Label>Period</Label>
                    <PeriodFilter value={period} onChange={this.handlePeriodChange} />
                  </Col>
                  <Col md={4} className="text-md-right">
                    <Button color="success" disabled={this.state.exporting} onClick={this.handleExport}>
                      {this.state.exporting ? <Spinner size="sm" className="mr-1" /> : <i className="ri-file-excel-2-line mr-1"></i>} Export XLS
                    </Button>
                    <Link to="/analytics" className="btn btn-outline-secondary ml-2">
                      Back to Dashboard
                    </Link>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2">Loading coupon analytics...</p>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <Row>
                  <Col md={3}>
                    <Card className="bg-primary text-white">
                      <CardBody>
                        <h6 className="text-white-50">Total Coupons Used</h6>
                        <h3>{this.formatNumber(summary.totalUsage || summary.totalUsed)}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-success text-white">
                      <CardBody>
                        <h6 className="text-white-50">Total Discount Given</h6>
                        <h3>{this.formatCurrency(summary.totalDiscountGiven || summary.totalDiscount)}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-info text-white">
                      <CardBody>
                        <h6 className="text-white-50">Unique Users</h6>
                        <h3>{this.formatNumber(summary.uniqueUsers)}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="bg-warning text-white">
                      <CardBody>
                        <h6 className="text-white-50">Total Order Value</h6>
                        <h3>{this.formatCurrency(summary.totalOrderValue)}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  {/* Top Coupons */}
                  <Col md={6}>
                    <Card>
                      <CardBody>
                        <h4 className="card-title mb-4">Top Performing Coupons</h4>
                        {topCoupons.length === 0 ? (
                          <p className="text-muted text-center">No data available</p>
                        ) : (
                          <Table className="table-sm mb-0">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Code</th>
                                <th className="text-center">Type</th>
                                <th className="text-right">Uses</th>
                                <th className="text-right">Discount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {topCoupons.slice(0, 10).map((coupon, index) => (
                                <tr key={coupon._id || index}>
                                  <td>{index + 1}</td>
                                  <td>
                                    <strong>{coupon.code}</strong>
                                  </td>
                                  <td className="text-center">
                                    <Badge color="info">-</Badge>
                                  </td>
                                  <td className="text-right">{this.formatNumber(coupon.usage || coupon.usageCount)}</td>
                                  <td className="text-right">{this.formatCurrency(coupon.discountGiven || coupon.totalDiscount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </CardBody>
                    </Card>
                  </Col>

                  {/* Usage by Category */}
                  <Col md={6}>
                    <Card>
                      <CardBody>
                        <h4 className="card-title mb-4">Usage by Category</h4>
                        {usageByCategory.length === 0 ? (
                          <p className="text-muted text-center">No data available</p>
                        ) : (
                          <>
                            <Table className="table-sm mb-3">
                              <thead>
                                <tr>
                                  <th>Category</th>
                                  <th className="text-right">Uses</th>
                                  <th className="text-right">Discount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {usageByCategory.map((item, index) => (
                                  <tr key={index}>
                                    <td>
                                      <Badge color={this.getCategoryBadgeColor(item._id)}>
                                        {item._id || "Unknown"}
                                      </Badge>
                                    </td>
                                    <td className="text-right">{this.formatNumber(item.usage || item.count)}</td>
                                    <td className="text-right">{this.formatCurrency(item.discountGiven || item.totalDiscount)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                            {/* Visual representation */}
                            {usageByCategory.map((item, index) => {
                              const maxCount = Math.max(...usageByCategory.map(c => c.usage || c.count || 0), 1);
                              const itemCount = item.usage || item.count || 0;
                              const percentage = (itemCount / maxCount) * 100;
                              return (
                                <div key={index} className="mb-2">
                                  <div className="d-flex justify-content-between mb-1">
                                    <small>{item._id || "Unknown"}</small>
                                    <small>{this.formatNumber(itemCount)}</small>
                                  </div>
                                  <Progress
                                    value={percentage}
                                    color={this.getCategoryBadgeColor(item._id)}
                                    style={{ height: "8px" }}
                                  />
                                </div>
                              );
                            })}
                          </>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Usage Trend */}
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Recent Usage Trend</h4>
                    {usageTrend.length === 0 ? (
                      <p className="text-muted text-center">No data available</p>
                    ) : (
                      <Table className="table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th className="text-right">Uses</th>
                            <th className="text-right">Discount Given</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usageTrend.slice(-10).map((item, index) => (
                            <tr key={index}>
                              <td>{item._id}</td>
                              <td className="text-right">{this.formatNumber(item.usage || item.count)}</td>
                              <td className="text-right">{this.formatCurrency(item.discountGiven || item.totalDiscount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </CardBody>
                </Card>
              </>
            )}
          </Container>
        </div>
      </React.Fragment>
    );
  }
}

export default withRouter(withTranslation()(CouponAnalytics));
